import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { renderMarkdown } from '../../../utils/markdown';
import AdminService from '../../../API/AdminService';
import UserService from '../../../API/UserService';
import { ADMIN_addres } from '../../../API/API';
import styles from './FAQEditor.module.css';
import logger from '../../../utils/logger';
const FAQEditor = () => {
    const navigate = useNavigate();
    const { articleId } = useParams();
    const [searchParams] = useSearchParams();
    const categoryFromUrl = searchParams.get('category');
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        summary: '',
        featured_image: '',
        category_id: '',
        is_published: true
    });

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [errors, setErrors] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // НОВОЕ: Состояние для отслеживания загрузки пользователя
    const [isLoadingUser, setIsLoadingUser] = useState(true);

    const [showPreview, setShowPreview] = useState(false);
    const isEditing = !!articleId;

    // Парсинг markdown вынесен в src/utils/markdown.js — там настроен
    // строгий DOMPurify whitelist и единая конфигурация marked.
    const parseMarkdown = renderMarkdown;

    useEffect(() => {
        loadInitialData();
        loadCurrentUser();
    }, [articleId]);

    // ИСПРАВЛЕНО: Добавлено состояние загрузки
    const loadCurrentUser = async () => {
        try {
            setIsLoadingUser(true);
            const userInfo = await UserService.getMyProfile();
            setCurrentUser(userInfo);

            if (!userInfo || userInfo.email !== 'min@ya.ru') {
                alert('У вас нет прав для создания/редактирования статей FAQ');
                navigate('/main/faq');
                return;
            }
        } catch (error) {
            logger.error('Ошибка получения профиля пользователя:', error);
            navigate('/main/faq');
        } finally {
            setIsLoadingUser(false);
        }
    };

    const loadInitialData = async () => {
        try {
            setLoading(true);
            const categoriesResponse = await AdminService.getFAQCategories();
            setCategories(categoriesResponse.data || []);

            if (isEditing) {
                const articleResponse = await AdminService.getFAQArticle(articleId);
                const article = articleResponse.data;
                setFormData({
                    title: article.title || '',
                    content: article.content || '',
                    summary: article.summary || '',
                    featured_image: article.featured_image || '',
                    category_id: article.category.id || '',
                    is_published: article.is_published !== undefined ? article.is_published : true
                });
            } else if (categoryFromUrl) {
                const category = categoriesResponse.data.find(cat => cat.name === categoryFromUrl);
                if (category) {
                    setFormData(prev => ({ ...prev, category_id: category.id }));
                }
            }
        } catch (error) {
            logger.error('Ошибка загрузки данных:', error);
            setErrors(['Ошибка загрузки данных']);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors.length > 0) {
            setErrors([]);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Пожалуйста, выберите файл изображения');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Размер файла не должен превышать 5MB');
            return;
        }

        try {
            setImageUploading(true);
            const response = await AdminService.uploadArticleImage(file);
            const fullImageUrl = `${ADMIN_addres}${response.image_url}`;

            const textarea = document.querySelector('.editor-content');
            const imageMarkdown = `\n![${file.name}](${fullImageUrl})\n`;

            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const newContent = formData.content.substring(0, start) + imageMarkdown + formData.content.substring(end);

                setFormData(prev => ({ ...prev, content: newContent }));

                setTimeout(() => {
                    textarea.selectionStart = textarea.selectionEnd = start + imageMarkdown.length;
                    textarea.focus();
                }, 0);
            }
        } catch (error) {
            logger.error('Ошибка загрузки изображения:', error);
            alert('Ошибка загрузки изображения');
        } finally {
            setImageUploading(false);
        }
    };

    const insertImageAtCursor = () => {
        fileInputRef.current?.click();
    };

    const insertFormatting = (format) => {
        const textarea = document.querySelector('.editor-content');
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = formData.content.substring(start, end);
        let replacement = '';

        switch (format) {
            case 'bold':
                replacement = `**${selectedText || 'жирный текст'}**`;
                break;
            case 'italic':
                replacement = `*${selectedText || 'курсив'}*`;
                break;
            case 'heading':
                replacement = `## ${selectedText || 'Заголовок'}`;
                break;
            case 'link':
                replacement = `[${selectedText || 'текст ссылки'}](URL)`;
                break;
            case 'list':
                replacement = `\n- ${selectedText || 'пункт списка'}\n- пункт 2\n- пункт 3\n`;
                break;
            case 'quote':
                replacement = `> ${selectedText || 'цитата'}`;
                break;
            case 'code':
                replacement = `\`${selectedText || 'код'}\``;
                break;
            default:
                return;
        }

        const newContent = formData.content.substring(0, start) + replacement + formData.content.substring(end);
        setFormData(prev => ({ ...prev, content: newContent }));

        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = start + replacement.length;
        }, 0);
    };

    const handleFeaturedImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            alert('Пожалуйста, выберите файл изображения');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert('Размер файла не должен превышать 5MB');
            return;
        }

        try {
            setImageUploading(true);
            const response = await AdminService.uploadArticleImage(file);
            const fullImageUrl = `${ADMIN_addres}${response.image_url}`;
            setFormData(prev => ({ ...prev, featured_image: fullImageUrl }));
        } catch (error) {
            logger.error('Ошибка загрузки обложки:', error);
            alert('Ошибка загрузки обложки');
        } finally {
            setImageUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = AdminService.validateFAQArticleData(formData);
        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            setLoading(true);
            setErrors([]);

            if (isEditing) {
                await AdminService.updateFAQArticle(articleId, formData);
                alert('Статья обновлена');
                navigate(`/main/faq/articles/${articleId}`);
            } else {
                const response = await AdminService.createFAQArticle(formData);
                alert('Статья создана');
                navigate(`/main/faq/articles/${response.article_id}`);
            }
        } catch (error) {
            logger.error('Ошибка сохранения статьи:', error);
            setErrors(['Ошибка сохранения статьи']);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (isEditing) {
            navigate(`/main/faq/articles/${articleId}`);
        } else if (categoryFromUrl) {
            navigate(`/main/faq/category/${categoryFromUrl}`);
        } else {
            navigate('/main/faq');
        }
    };

    // ИСПРАВЛЕНО: Сначала проверяем состояние загрузки пользователя
    if (isLoadingUser) {
        return (
            <div className={styles.loadingContainer}>
                <p>Проверка прав доступа...</p>
            </div>
        );
    }

    // ИСПРАВЛЕНО: Проверяем права доступа только после загрузки
    if (!currentUser || currentUser.email !== 'min@ya.ru') {
        logger.log(currentUser);

        return (
            <div className={styles.errorContainer}>
                <h2>Доступ запрещен</h2>
                <p>У вас нет прав для создания/редактирования статей FAQ</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <p>Загрузка статьи...</p>
            </div>
        );
    }

    return (
        <div className={styles.editorContainer}>
            <div className={styles.editorHeader}>
                <h1>{isEditing ? 'Редактировать статью' : 'Создать статью'}</h1>
            </div>

            {errors.length > 0 && (
                <div className={styles.errorContainer}>
                    {errors.map((error, index) => (
                        <p key={index} className={styles.error}>{error}</p>
                    ))}
                </div>
            )}

            <form onSubmit={handleSubmit} className={styles.editorForm}>
                {/* Заголовок */}
                <div className={styles.formGroup}>
                    <label htmlFor="title">Заголовок *</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Введите заголовок статьи"
                        required
                    />
                </div>

                {/* Краткое описание */}
                <div className={styles.formGroup}>
                    <label htmlFor="summary">Краткое описание *</label>
                    <textarea
                        id="summary"
                        name="summary"
                        value={formData.summary}
                        onChange={handleInputChange}
                        placeholder="Краткое описание для превью (максимум 200 символов)"
                        maxLength="200"
                        rows="3"
                        required
                    />
                    <span className={styles.charCount}>
                        {formData.summary.length}/200
                    </span>
                </div>

                {/* Категория */}
                <div className={styles.formGroup}>
                    <label htmlFor="category_id">Категория *</label>
                    <select
                        id="category_id"
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                    >
                        <option value="">Выберите категорию</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Обложка статьи */}
                <div className={styles.formGroup}>
                    <label htmlFor="featured_image">Обложка статьи</label>
                    <input
                        type="file"
                        id="featured_image"
                        accept="image/*"
                        onChange={handleFeaturedImageUpload}
                        disabled={imageUploading}
                    />
                    {formData.featured_image && (
                        <div className={styles.imagePreview}>
                            <img src={formData.featured_image} alt="Обложка" />
                        </div>
                    )}
                </div>

                {/* Редактор контента */}
                <div className={styles.formGroup}>
                    <label>Содержание статьи (Markdown) *</label>

                    <div className={styles.editorToolbar}>
                        <button
                            type="button"
                            onClick={() => insertFormatting('bold')}
                            className={styles.toolbarButton}
                            title="Жирный текст"
                        >
                            <strong>B</strong>
                        </button>
                        <button
                            type="button"
                            onClick={() => insertFormatting('italic')}
                            className={styles.toolbarButton}
                            title="Курсив"
                        >
                            <em>I</em>
                        </button>
                        <button
                            type="button"
                            onClick={() => insertFormatting('heading')}
                            className={styles.toolbarButton}
                            title="Заголовок"
                        >
                            H
                        </button>
                        <button
                            type="button"
                            onClick={() => insertFormatting('link')}
                            className={styles.toolbarButton}
                            title="Ссылка"
                        >
                            🔗
                        </button>
                        <button
                            type="button"
                            onClick={() => insertFormatting('list')}
                            className={styles.toolbarButton}
                            title="Список"
                        >
                            ≡
                        </button>
                        <button
                            type="button"
                            onClick={() => insertFormatting('quote')}
                            className={styles.toolbarButton}
                            title="Цитата"
                        >
                            "
                        </button>
                        <button
                            type="button"
                            onClick={() => insertFormatting('code')}
                            className={styles.toolbarButton}
                            title="Код"
                        >
                            {'<>'}
                        </button>
                        <button
                            type="button"
                            onClick={insertImageAtCursor}
                            className={styles.toolbarButton}
                            disabled={imageUploading}
                            title="Вставить изображение"
                        >
                            🖼️ {imageUploading ? '...' : ''}
                        </button>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept="image/*"
                        onChange={handleImageUpload}
                    />

                    <div className={styles.editorTabs}>
                        <button
                            type="button"
                            className={!showPreview ? styles.activeTab : ''}
                            onClick={() => setShowPreview(false)}
                        >
                            Редактор
                        </button>
                        <button
                            type="button"
                            className={showPreview ? styles.activeTab : ''}
                            onClick={() => setShowPreview(true)}
                        >
                            Предпросмотр
                        </button>
                    </div>

                    {!showPreview ? (
                        <textarea
                            className="editor-content"
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            placeholder="Напишите содержание статьи в формате Markdown..."
                            rows="20"
                            required
                        />
                    ) : (
                        <div 
                            className={styles.markdownPreview}
                            dangerouslySetInnerHTML={{ __html: parseMarkdown(formData.content) }}
                        />
                    )}
                </div>

                {/* Публикация */}
                <div className={styles.formGroup}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            name="is_published"
                            checked={formData.is_published}
                            onChange={handleInputChange}
                        />
                        <span>Опубликовать статью</span>
                    </label>
                </div>

                {/* Кнопки действий */}
                <div className={styles.formActions}>
                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={loading || imageUploading}
                    >
                        {loading ? 'Сохранение...' : (isEditing ? 'Обновить статью' : 'Создать статью')}
                    </button>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className={styles.cancelButton}
                        disabled={loading}
                    >
                        Отмена
                    </button>
                </div>
            </form>
        </div>
    );
};

export default FAQEditor;
